import { useQuery } from "@tanstack/react-query";
import Drawer from "../../../../../shared/Drawer";
import { fetchNotificationDetails } from "../../../../../../utils/apiUtils";
import { NOTIFICATION_DETAILS_QUERY_KEY_ROOT } from "../../constants";
import GeneralNotificationInfo from "./GeneralNotificationInfo";
import NotificationErrors from "./NotificationErrors";
import MemberDetails from "./MemberDetails";
import NotificationDetailsSheetBottomBar from "./NotificationDetailsSheetBottomBar";
import NotificationPreview from "./NotificationPreview";
import "./NotificationDetailsSheet.scss";

/**
 * Right-side drawer with notification details: summary grid, optional errors panel, member summary, rendered/raw preview, and footer actions.
 * Fetches details when `open` and `notificationId` are set.
 * @param {object} props
 * @param {boolean} props.open Radix dialog open state.
 * @param {(open: boolean) => void} props.onOpenChange Radix open-state callback.
 * @param {string | null} props.notificationId Selected list row id (message UUID).
 * @param {Record<string, unknown>} props.listRow Selected queue row from `GET /notifications` (client, template, plan, sentTimestamp, scheduledTimestamp, type, status); matches the row the user opened from the list.
 * @param {string} props.sendType Selected send type (`automated` | `manual`) for the sheet footer actions.
 * @param {string | null} props.queueFilter Active queue tab ({@link QueueFilter}).
 * @param {() => void} props.refreshNotificationsList Resets the queue list + count for current filters (same as TopBar refresh control).
 * @returns {import('react').JSX.Element}
 */
const NotificationDetailsSheet = ({
    open,
    onOpenChange,
    notificationId,
    listRow,
    sendType,
    queueFilter,
    refreshNotificationsList,
}) => {
    const query = useQuery({
        queryKey: [NOTIFICATION_DETAILS_QUERY_KEY_ROOT, notificationId],
        queryFn: () => fetchNotificationDetails({ notificationId }),
        enabled: Boolean(open && notificationId),
    });

    const details = query.data;

    const showLoadingError =
        !query.isLoading &&
        (query.isError || (!details && Boolean(notificationId)));

    const showDetails = !query.isLoading && !query.isError && !!details;

    const showErrors =
        details && Array.isArray(details.errors) && details.errors.length > 0;

    return (
        <Drawer
            open={open}
            onOpenChange={onOpenChange}
            title="Notification details"
            description="Technical and delivery information for this notification."
            bodyClassName="notification-details-sheet"
            footer={
                <NotificationDetailsSheetBottomBar
                    sendType={sendType}
                    actionsDisabled={query.isLoading}
                    notificationId={notificationId}
                    listRow={listRow}
                    details={details}
                    status={details?.status ?? listRow?.status}
                    queueFilter={queueFilter}
                    refreshNotificationsList={refreshNotificationsList}
                    onDeleteSuccess={() => {
                        onOpenChange(false);
                    }}
                />
            }
        >
            <GeneralNotificationInfo
                listRow={listRow}
                details={details}
                queueFilter={queueFilter}
            />
            {showErrors && <NotificationErrors errors={details.errors} />}
            {query.isLoading && (
                <div
                    className="notification-details-sheet__status"
                    role="status"
                    aria-live="polite"
                >
                    <span>Loading notification details</span>
                    <span className="loader loader--inline" aria-hidden />
                </div>
            )}
            {showLoadingError && (
                <p className="notification-details-sheet__error">
                    Error loading notification details.
                </p>
            )}
            {showDetails && (
                <>
                    <MemberDetails details={details} />
                    <NotificationPreview
                        notificationId={notificationId}
                        details={details}
                    />
                </>
            )}
        </Drawer>
    );
};

export default NotificationDetailsSheet;
